import React, { useState, useEffect, useRef } from 'react';
import { Collapse, List, Checkbox, Button, Typography, Space, message } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * InputSelectableList component for displaying a list with selectable items
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing list items
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 * @param {string} props.sessionId - The current session ID
 * @param {string} props.endpointUrl - The endpoint URL to submit selections to
 */
const InputSelectableList = ({
  data,
  title = "Selectable List Items",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent,
  sessionId,
  endpointUrl,
  onComponentUpdate,
  project_id,
  agent_key,
  onProcessingStart,
  onProcessingEnd
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // State to track selected items
  const [selectedItems, setSelectedItems] = useState([]);
  
  // State to track loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize selections when data changes
  useEffect(() => {
    if (data && data.list_items) {
      // Reset selections when data changes
      setSelectedItems([]);
    }
  }, [data]);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };
  


  // Use useEffect to update the state after render
  const expandIconRef = useRef(null);

  // Add useEffect to handle expand state changes
  useEffect(() => {
    if (expandIconRef.current !== null) {
      setIsExpanded(expandIconRef.current);
      expandIconRef.current = null;
    }
  }, []);



  // Handle checkbox change
  const handleCheckboxChange = (item, index, checked) => {
    setSelectedItems(prev => {
      if (checked) {
        return [...prev, { item, index }];
      } else {
        return prev.filter(selected => selected.index !== index);
      }
    });
  };
  



// Initialize selections when data changes
useEffect(() => {
  if (data && data.list_items) {
    // Check for pre-selected items
    const initialSelections = data.list_items
      .map((item, index) => {
        // If the 4th element is true, this item should be pre-selected
        if (item.length >= 4 && item[3] === true) {
          return { item, index };
        }
        return null;
      })
      .filter(Boolean); // Remove null entries
    
    setSelectedItems(initialSelections);
  }
}, [data]);  



  // Handle submit button click
// Update the handleSubmit function in InputSelectableList.jsx
const handleSubmit = async () => {
  if (selectedItems.length === 0) {
    message.warning('Please select at least one item');
    return;
  }

  
  if (!sessionId || !endpointUrl) {
    message.error('Missing session ID or endpoint URL');
    return;
  }

  
  // Call progress start
  if (onProcessingStart) {
    onProcessingStart();
  }

  try {
    setIsSubmitting(true);
    
    // Extract the selected item data
    const selectedData = selectedItems.map(({ item, index }) => ({
      item,
      index
    }));
    
    // Get the agent_key from localStorage
    // First try to get it for the active agent
    //const activeAgentId = localStorage.getItem(`active_agent_${sessionId}`);
    //console.log('Got ', `active_agent_${sessionId}`, ' as ', activeAgentId)
    //const agent_key = localStorage.getItem(`agent_key_${activeAgentId}`);
    console.log('Got agent_key as ', agent_key)
    
    // Try to retrieve the component_map from localStorage
    let component_map = null;
    try {
      const componentMapKey = `component_map_${agent_key}`;
      const componentMapJson = localStorage.getItem(componentMapKey);
      if (componentMapJson) {
        component_map = JSON.parse(componentMapJson);
      }
    } catch (e) {
      console.warn('Failed to retrieve component_map from localStorage:', e);
    }

    // Prepare the payload
    const payload = {
      sessionId,
      componentId: uuid,
      componentType: 'input_selectable_list',
      data: {
        selected: selectedData,
        originalTitle: title
      },
      agent_key: agent_key, // Include the agent_key in the request
      component_map: component_map,
      project_id: project_id || "11111111-1111-1111-1111-111111111111",
    };
    
    console.log('Sending policy evaluation request with agent_key:', agent_key); 
    
    // Build the correct base URL - extract just the domain and port
    const baseUrl = endpointUrl.split('/').slice(0, 3).join('/');
    const policyEndpoint = `${baseUrl}/check_policy_with_nodes`;

    // Send the data to the new endpoint
    const response = await fetch( policyEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`
      },
      body: JSON.stringify(payload),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Submission failed with status ${response.status}`);
    }
    
    const result = await response.json();

    console.log('>>>>>>>>>   ', result)
    
    // Handle success
    message.success(result.message || 'Policy evaluation completed');
    
    // If there are updated components, handle them
    if (result.updatedComponentIds && result.updatedComponentIds.length > 0) {
      console.log('Components updated:', result.updatedComponentIds);
    }

    // Call the onComponentUpdate prop with the full result
    if (onComponentUpdate && typeof onComponentUpdate === 'function') {
      onComponentUpdate(result);
    }


  } catch (error) {
    console.error('Error submitting selections:', error);
    message.error(`Failed to submit: ${error.message}`);
  } finally {
    setIsSubmitting(false);
    if (onProcessingEnd) {
      onProcessingEnd();
    }    
  }
};


  
  
  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        // This will trigger a re-render
        setIsExpanded(prev => prev);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  return (
    <div style={{ 
      marginBottom: '4px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          //setIsExpanded(isActive);
          expandIconRef.current = isActive;
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px'  }}>
                {data?.list_title || title}
              </div>
            ),
            children: (
              <div>
                {/* Optional Description */}
                {data?.description && (
                  <Text style={{ display: 'block', marginBottom: '16px' }}>
                    {data.description}
                  </Text>
                )}
                
                {/* List Items with Checkboxes */}
                {data && data.list_items && data.list_items.length > 0 ? (
                  <>
                    <List
                      itemLayout="horizontal"
                      dataSource={data.list_items}
                      renderItem={(item, index) => {
                        const [prefix, mainLine, subLine] = item;
                        const isSelected = selectedItems.some(selected => selected.index === index);
                        
                        return (
                          <List.Item key={index} style={{ padding: '8px 0' }}>
                            <Space>
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleCheckboxChange(item, index, e.target.checked)}
                              />
                              <div>
                                <div style={{ fontWeight: 'bold' }}>{mainLine}</div>
                                {subLine && <div style={{ color: '#666' }}>{subLine}</div>}
                              </div>
                            </Space>
                          </List.Item>
                        );
                      }}
                    />
                    
                    {/* Submit Button */}
                    <div style={{ marginTop: '16px', textAlign: 'right' }}>
                      <Button
                        type="primary"
                        loading={isSubmitting}
                        onClick={handleSubmit}
                        style={{
                          backgroundColor: '#6200EE',
                          borderColor: '#6200EE'
                        }}
                      >
                        Submit Selection
                      </Button>
                    </div>
                  </>
                ) : (
                  <Text>No list items available.</Text>
                )}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </Button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <Button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default InputSelectableList;